import { CommonModule } from '@angular/common';
import { Component, inject, input, signal, type InputSignal, type TemplateRef, type WritableSignal } from '@angular/core';
import { Card } from '../../../../shared/components/card/card/card';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import type { ChartConfiguration } from 'chart.js';
import { lineChartDefaultOptions } from '../../../../interfaces/charts';
import type { DashboardType, FinancialSupportMetrics, FinancialSupportMetricsTicketsSolvedItem } from '../../../../interfaces/dashboard';
import { DashboardService } from '../../../../services/dashboard.service';
import { SocketService } from '../../../../services/socket.service';
import { UIChart } from 'primeng/chart';
import { EmptyChart } from "../../../../shared/components/empty-chart/empty-chart";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SkeletonModule } from "primeng/skeleton";
import { finalize } from 'rxjs';
import dayjs from 'dayjs';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-dashboard-support-risk',
  imports: [
    CommonModule,
    Card,
    CardHeader,
    UIChart,
    EmptyChart,
    ProgressSpinnerModule,
    SkeletonModule,
    DialogModule
],
  templateUrl: './dashboard-support-risk.html',
  styleUrl: './dashboard-support-risk.css',
})
export class DashboardSupportRisk {

  private readonly _dashboardService: DashboardService = inject(DashboardService);
  private readonly _socketService: SocketService = inject(SocketService);

  public startDateFilter: InputSignal<Date|null> = input<Date|null>(null);
  public endDateFilter: InputSignal<Date|null> = input<Date|null>(null);

  public dialogVisible: boolean = false;
  public dialogTitle: WritableSignal<string> = signal<string>('');
  public selectedDialogChart: WritableSignal<TemplateRef<any>|null> = signal<TemplateRef<any>|null>(null);

  public supportMetricsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public supportMetricsData: WritableSignal<FinancialSupportMetrics|null> = signal<FinancialSupportMetrics|null>(null);

  public supportTicketsSolvedLoading: WritableSignal<boolean> = signal<boolean>(false);
  public supportTicketsSolvedData: WritableSignal<any> = signal<any>(null);
  public supportTicketsSolvedChartData: WritableSignal<any> = signal<any>(null);
  public supportTicketsSolvedOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...lineChartDefaultOptions});

  public ngOnInit(): void {
    this._initSocket();
  }

  private _initSocket(): void {
    this._socketService.echo
      .channel('dashboard')
      .listen('.updated', (data: { dashboard: DashboardType }) => console.log({data}));
  }

  private _initDashboard(): void {
    this.getSupportSupportMetrics();
  }

  public ngOnChanges(): void {
    if(!this._isCompletedRangeDates()) {
      return;
    }

    this._initDashboard();
  }

  private _isCompletedRangeDates(): boolean {
    return this.startDateFilter() instanceof Date && this.endDateFilter() instanceof Date;
  }

  public openDialog(template: TemplateRef<any>, title: string): void {
    this.dialogTitle.set(title);
    this.selectedDialogChart.set(template);
    this.dialogVisible = true;
  }

  public closeDialog(): void {
    this.dialogVisible = false;
    this.dialogTitle.set('');
    this.selectedDialogChart.set(null);
  }

  public getSupportSupportMetrics(): void {
    this.supportMetricsLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialSupportMetrics(filters)
      .pipe(finalize(() => this.supportMetricsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.supportMetricsData.set(response.data);

            this.mountSupportTicketsSolvedData();
            this.mountSupportTicketsSolvedOptions();
          }
        }
      })
  }

  public mountSupportTicketsSolvedData(): void {
    this.supportTicketsSolvedChartData.set({
      labels: this.supportMetricsData()?.chamados_resolvidos.por_dia.map((item: FinancialSupportMetricsTicketsSolvedItem) => dayjs(item.date).format('DD/MM')),
      datasets: [
        {
          label: 'Resolvidos',
          data: this.supportMetricsData()?.chamados_resolvidos.por_dia.map((item: FinancialSupportMetricsTicketsSolvedItem) => item.resolved),
          borderWidth: 2,
          backgroundColor: '#6DA4FF66',
          borderColor: '#6DA4FF',
          pointRadius: 1.5,
          lineTension: 0.4,
        },
        {
          label: 'Todos',
          data: this.supportMetricsData()?.chamados_resolvidos.por_dia.map((item: FinancialSupportMetricsTicketsSolvedItem) => item.total),
          borderWidth: 2,
          backgroundColor: '#4FCF7566',
          borderColor: '#4FCF75',
          pointRadius: 1.5,
          lineTension: 0.4,
        },
      ]
    });
  }

  public mountSupportTicketsSolvedOptions(): void {

    this.supportTicketsSolvedOptions.set({
      ...lineChartDefaultOptions,
      maintainAspectRatio: false,
      scales: {
        ...lineChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000) {
                return `${(+value / 1000)}K`;
              }

              return `${value}`
            }
          },
          beginAtZero: true
        },
      },
      plugins: {
        ...lineChartDefaultOptions?.plugins,
        tooltip: {
          ...lineChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: function(context: any) {
              return `${context.dataset.label}: ${context.raw}`;
            }
          }
        }
      },
    });
  }
}
